// @ts-check
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Row, Widget } from 'shared/containers/portal_layout';
import {
  reduxFormWithDebugging,
  SubmissionError,
  Field,
} from 'utils/reduxFormWrapper';
import {
  SelectField,
  TextField,
  PlainSelectField,
  StyledLabelAndError,
} from 'shared/containers/portal_layout/HForm';
import Errors from 'shared/components/Errors';
import api from 'utils/api';
import LoadingContent from 'shared/components/LoadingContent';
import FlashMessageActions from 'shared/actions/FlashMessageActions';
import { RawTextInput } from 'shared/containers/portal_layout/HForm/TextField';
import Stripe from '%stripe%'; // This actually comes from "externals", see webpack.config.js
import SpinnerButton from 'shared/components/SpinnerButton';
import {
  createValidator,
  required,
  validCVC,
  validCC,
  validCCExp,
} from 'utils/validation';

// Generate expiration year options from 2016 to one year beyond current year
const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const endYear = currentYear + 1;
  const years = [];

  for (let year = 2016; year <= endYear; year++) {
    years.push({
      label: year.toString(),
      value: year.toString().slice(-2), // Last 2 digits
    });
  }

  return years;
};

const RawCreditCardField = (fieldProps) => {
  const cardType = Stripe.card.cardType(fieldProps.input.value);
  return (
    <StyledLabelAndError
      inputId="number"
      label="Credit Card Number"
      meta={fieldProps.meta}
    >
      <RawTextInput placeholder="Credit Card Number" {...fieldProps.input} />
      &nbsp;{cardType !== 'Unknown' ? cardType : null}
    </StyledLabelAndError>
  );
};

const PresentationComponent = reduxFormWithDebugging({})((props) => {
  if (props.isLoading) {
    return (
      <Page title="Subscribe">
        <Row>
          <Widget span={12} title="Subscribe to AristaMD">
            <LoadingContent />
          </Widget>
        </Row>
      </Page>
    );
  } else if (props.subscribed) {
    return (
      <Page title="Subscribe">
        <Row>
          <Widget span={12} title="Subscribe to AristaMD">
            <p>You are subscribed!</p>
            <p>
              To unsubscribe, please email us at{' '}
              <a href="mailto:support@preferral.com">support@preferral.com</a>.
            </p>
          </Widget>
        </Row>
      </Page>
    );
  } else {
    return (
      <Page title="Subscribe">
        <Row>
          <Widget span={12} title="Subscribe to AristaMD">
            <div>
              <p>
                You can subscribe to AristaMD for ${props.planAmount}/month per
                provider. Your credit card will be automatically charged each
                month.
              </p>

              {props.quantity === 0 ? (
                <p>
                  Since none of your providers have received referrals yet, your
                  card will not be charged today.
                </p>
              ) : (
                <p>
                  Once subscribed, you will be billed for {props.quantity}{' '}
                  {props.quantity > 1 ? 'seats' : 'seat'}. Seats will be added
                  as the number of providers receiving referrals on AristaMD
                  surpasses the number of subscribed seats. Your subscription
                  will begin at ${props.totalAmount}/month starting today.
                </p>
              )}

              <p>Please enter your credit card information below.</p>
              <form className="form-horizontal" onSubmit={props.handleSubmit}>
                <Errors errors={props.error || []} />
                <Field name="number" component={RawCreditCardField} />
                <PlainSelectField
                  label="Expiration Month"
                  name="exp_month"
                  options={[
                    { label: '01 January', value: '01' },
                    { label: '02 February', value: '02' },
                    { label: '03 March', value: '03' },
                    { label: '04 April', value: '04' },
                    { label: '05 May', value: '05' },
                    { label: '06 June', value: '06' },
                    { label: '07 July', value: '07' },
                    { label: '08 August', value: '08' },
                    { label: '09 September', value: '09' },
                    { label: '10 October', value: '10' },
                    { label: '11 November', value: '11' },
                    { label: '12 DecemberMarch', value: '12' },
                  ]}
                  className="input-small"
                />
                <PlainSelectField
                  label="Expiration Year"
                  name="exp_year"
                  options={generateYearOptions()}
                  className="input-small"
                />
                <TextField label="CVC" name="cvc" />
                <TextField label="Cardholder Name" name="name" />
                <TextField label="Address" name="address_line1" />
                <TextField label="Address (line 2)" name="address_line2" />
                <TextField label="City" name="address_city" />
                <SelectField
                  label="State"
                  name="address_state"
                  options={props.states}
                />
                <TextField label="Zip" name="address_zip" />
                <div className="form-actions">
                  <SpinnerButton
                    type="submit"
                    className="btn btn-primary"
                    loading={props.submittingForm}
                  >
                    Subscribe!
                  </SpinnerButton>
                </div>
              </form>
            </div>
          </Widget>
        </Row>
      </Page>
    );
  }
});

const validateForm = createValidator({
  number: validCC,
  exp_month: [required, validCCExp('exp_month', 'exp_year')],
  exp_year: [required, validCCExp('exp_month', 'exp_year')],
  cvc: validCVC,
  name: required,
  address_line1: required,
  address_city: required,
  address_state: required,
  address_zip: required,
});

class NewSubscriptionPage extends Component {
  constructor(props, context) {
    super(props, context);
    this.createSubscription = this.createSubscription.bind(this);
    this.state = {
      submittingForm: false,
      subscribed: false,
      states: null,
      fetchedStates: false,
      fetchedSubscription: false,
      planAmount: null,
      quantity: null,
      totalAmount: null,
    };
  }

  componentDidMount() {
    this.fetchSubscription();
    this.fetchStates();
  }

  fetchSubscription() {
    api.get('/agent_portal/api/subscription').then((response) => {
      if (response.data.subscribed) {
        this.setState({
          fetchedSubscription: true,
          subscribed: true,
        });
      } else {
        Stripe.setPublishableKey(response.data.publishableStripeKey);
        if (response.data.stripeEndpointOverride) {
          Stripe.endpoint = response.data.stripeEndpointOverride;
        }
        this.setState({
          fetchedSubscription: true,
          subscribed: false,
          planAmount: response.data.planAmount,
          quantity: response.data.quantity,
          totalAmount: response.data.totalAmount,
        });
      }
    });
  }

  fetchStates() {
    // Get states for address select box
    return api.get('/shared/api/states.json').then((response) => {
      this.setState({
        fetchedStates: true,
        states: response.data.map((state) => ({
          label: state.name,
          value: state.name,
        })),
      });
    });
  }

  sendTokenToPreferral(paymentToken) {
    // After we have a token from stripe.js, we want to submit the token
    // to preferral for final submission to the Stripe API.  This is where the card
    // is actually checked and might be declined.
    return api
      .postWithoutDefaultRejectionHandling('/agent_portal/api/subscription', {
        payment_token: paymentToken,
        expected_total_amount: this.state.totalAmount,
      })
      .then(
        (response) => {
          FlashMessageActions.flashSuccess({
            message: 'You have successfully subscribed!',
          });
          this.setState({ subscribed: true });
        },
        (error) => {
          this.setState({ submittingForm: false });
          try {
            return error.defaultRejectionHandler(error);
          } catch (error) {
            // TODO: This is not the best solution overall, but a minimal change to get specs passing
            // The basic idea is always to raise SubmissionErrors (instead of axios errors) when we're being
            // consumed by handleSubmit.
            if (error instanceof SubmissionError) throw error;
            throw new SubmissionError({});
          }
        }
      );
  }

  createSubscription(formValues) {
    return new Promise((resolve, reject) => {
      const stripeResponseHandler = (status, response) => {
        if (status !== 200) {
          // console.log("GOT FAILED RESPONSE FROM STRIPE JS");
          // console.log(util.inspect(status))
          // console.log(util.inspect(response))
          // This is a failure with submission to stripe.js (e.g. credit card is invalid, not enough digits, expiration is missing, etc.)
          this.setState({ submittingForm: false });
          reject(new SubmissionError({ _error: response.error.message }));
          return;
        }
        // console.log("Token received was " , response.id);
        resolve(this.sendTokenToPreferral(response.id));
      };

      this.setState({ submittingForm: true });

      // Send credit card data to stripe via stripe.js
      Stripe.card.createToken(formValues, stripeResponseHandler);
    });
  }

  render() {
    return (
      <PresentationComponent
        form="Subscription"
        onSubmit={this.createSubscription}
        submittingForm={this.state.submittingForm}
        isLoading={
          !(this.state.fetchedStates && this.state.fetchedSubscription)
        }
        states={this.state.states}
        subscribed={this.state.subscribed}
        planAmount={this.state.planAmount}
        quantity={this.state.quantity}
        totalAmount={this.state.totalAmount}
        validate={validateForm}
      />
    );
  }
}

const NoNeedToSubscribePage = () => (
  <Page title="Subscription Not Required">
    <Row>
      <div className="well">
        <h4>Good News!</h4>
        <p>Your organization is not required to subscribe.</p>
      </div>
    </Row>
  </Page>
);

const MaybeSubscribeFormPage = ({ needsToSubscribe, ...props }) =>
  needsToSubscribe ? (
    <NewSubscriptionPage {...props} />
  ) : (
    <NoNeedToSubscribePage />
  );

const mapStateToProps = (state) => ({
  needsToSubscribe: state.currentAgent.currentEmployment.showSubscriptionLink,
});

export default connect(mapStateToProps)(MaybeSubscribeFormPage);
